import { useState, useMemo } from 'react';
import { Settings, RotateCcw, LayoutGrid, Filter, Newspaper, Package, Check, ArrowRight, CalendarX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetFooter,
    SheetClose,
    SheetDescription,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/hooks/useLanguage';
import {
    DashboardSettings,
    MetricsSettings,
    RansomwareFilters,
} from '@/hooks/useDashboardSettings';

interface DashboardSettingsDialogProps {
    settings: DashboardSettings;
    onToggleCard: (cardId: keyof Omit<DashboardSettings, 'metrics' | 'ransomwareFilters' | 'newsSources' | 'releaseProducts' | 'cardSizes' | 'layoutOrder'>) => void;
    onToggleMetric: (metricId: keyof MetricsSettings) => void;
    onSetRansomwareFilters: (filters: RansomwareFilters) => void;
    onSetNewsSources: (sources: string[]) => void;
    onSetReleaseProducts: (products: string[]) => void;
    onSetEolProducts: (products: string[]) => void; // New
    onReset: () => void;
    // Available options from data
    availableCountries: string[];
    availableGroups: string[];
    availableSourcesEN: string[];
    availableSourcesPL: string[];
    availableProducts: string[];
    availableEolProducts: string[]; // New
}

// Labels for cards
const CARD_LABELS: Record<string, { en: string; pl: string }> = {
    metricsRow: { en: 'Top Metrics Row', pl: 'Górny wiersz metryk' },
    cveMetrics: { en: 'CVE Metrics Row', pl: 'Wiersz metryk CVE' },
    mapWidget: { en: 'Ransomware Map', pl: 'Mapa Ransomware' },
    newsCard: { en: 'Latest News', pl: 'Najnowsze wiadomości' },
    ransomwareCard: { en: 'Recent Ransomware', pl: 'Ostatnie ransomware' },
    leaksCard: { en: 'Recent Leaks', pl: 'Ostatnie wycieki' },
    vulnerabilitiesCard: { en: 'Critical Vulnerabilities', pl: 'Krytyczne podatności' },
    highVulnerabilitiesCard: { en: 'High Vulnerabilities', pl: 'Wysokie podatności' },    // New
    mediumVulnerabilitiesCard: { en: 'Medium Vulnerabilities', pl: 'Średnie podatności' }, // New
    lowVulnerabilitiesCard: { en: 'Low Vulnerabilities', pl: 'Niskie podatności' },       // New
    eolCard: { en: 'End of Life', pl: 'Koniec wsparcia' },
    releasesCard: { en: 'Latest Releases', pl: 'Ostatnie wydania' },
};

export function DashboardSettingsDialog({
    settings,
    onToggleCard,
    onToggleMetric,
    onSetRansomwareFilters,
    onSetNewsSources,
    onSetReleaseProducts,
    onSetEolProducts,
    onReset,
    availableCountries,
    availableGroups,
    availableSourcesEN,
    availableSourcesPL,
    availableProducts,
    availableEolProducts,
}: DashboardSettingsDialogProps) {
    const { language } = useLanguage();
    const [searchCountry, setSearchCountry] = useState('');
    const [searchGroup, setSearchGroup] = useState('');
    const [searchProduct, setSearchProduct] = useState('');
    const [searchEolProduct, setSearchEolProduct] = useState('');

    // Filter available options by search
    const filteredCountries = useMemo(() =>
        availableCountries.filter(c => c.toLowerCase().includes(searchCountry.toLowerCase())),
        [availableCountries, searchCountry]
    );

    const filteredEolProducts = useMemo(() =>
        availableEolProducts.filter(p => p.toLowerCase().includes(searchEolProduct.toLowerCase())),
        [availableEolProducts, searchEolProduct]
    );

    const filteredGroups = useMemo(() =>
        availableGroups.filter(g => g.toLowerCase().includes(searchGroup.toLowerCase())),
        [availableGroups, searchGroup]
    );

    const filteredProducts = useMemo(() =>
        availableProducts.filter(p => p.toLowerCase().includes(searchProduct.toLowerCase())),
        [availableProducts, searchProduct]
    );

    const toggleCountry = (country: string) => {
        const current = settings.ransomwareFilters.countries;
        const newCountries = current.includes(country)
            ? current.filter(c => c !== country)
            : [...current, country];
        onSetRansomwareFilters({ ...settings.ransomwareFilters, countries: newCountries });
    };

    const toggleGroup = (group: string) => {
        const current = settings.ransomwareFilters.groups;
        const newGroups = current.includes(group)
            ? current.filter(g => g !== group)
            : [...current, group];
        onSetRansomwareFilters({ ...settings.ransomwareFilters, groups: newGroups });
    };

    const toggleSource = (source: string) => {
        const current = settings.newsSources;
        let nextList: string[];
        if (current.length === 0) {
            const allSources = [...availableSourcesEN, ...availableSourcesPL];
            nextList = allSources.filter(s => s !== source);
        } else if (current.includes('__NONE__')) {
            nextList = [source];
        } else {
            nextList = current.includes(source)
                ? current.filter(s => s !== source)
                : [...current, source];
        }

        const totalCount = availableSourcesEN.length + availableSourcesPL.length;
        if (nextList.length === totalCount) {
            nextList = [];
        }
        if (nextList.length === 0 && current.length === 1 && current.includes(source)) {
            nextList = ['__NONE__'];
        }

        onSetNewsSources(nextList);
    };

    const isSourceSelected = (source: string) => {
        if (settings.newsSources.length === 0) return true;
        if (settings.newsSources.includes('__NONE__')) return false;
        return settings.newsSources.includes(source);
    };

    const toggleProduct = (product: string) => {
        const current = settings.releaseProducts;
        let nextList: string[];
        if (current.length === 0) {
            nextList = availableProducts.filter(p => p !== product);
        } else if (current.includes('__NONE__')) {
            nextList = [product];
        } else {
            nextList = current.includes(product)
                ? current.filter(p => p !== product)
                : [...current, product];
        }

        if (nextList.length === availableProducts.length) nextList = [];
        if (nextList.length === 0 && current.length === 1 && current.includes(product)) nextList = ['__NONE__'];

        onSetReleaseProducts(nextList);
    };

    const isProductSelected = (product: string) => {
        if (settings.releaseProducts.length === 0) return true;
        if (settings.releaseProducts.includes('__NONE__')) return false;
        return settings.releaseProducts.includes(product);
    };

    const toggleEolProduct = (product: string) => {
        const current = settings.eolProducts;
        let nextList: string[];
        if (current.length === 0) {
            nextList = availableEolProducts.filter(p => p !== product);
        } else if (current.includes('__NONE__')) {
            nextList = [product];
        } else {
            nextList = current.includes(product)
                ? current.filter(p => p !== product)
                : [...current, product];
        }

        if (nextList.length === availableEolProducts.length) nextList = [];
        if (nextList.length === 0 && current.length === 1 && current.includes(product)) nextList = ['__NONE__'];

        onSetEolProducts(nextList);
    };

    const isEolProductSelected = (product: string) => {
        if (settings.eolProducts.length === 0) return true;
        if (settings.eolProducts.includes('__NONE__')) return false;
        return settings.eolProducts.includes(product);
    };

    // Select/Deselect All Helpers
    const setAllSources = (selected: boolean) => {
        onSetNewsSources(selected ? [] : ['__NONE__']);
    };

    const setAllEolProducts = (selected: boolean) => {
        onSetEolProducts(selected ? [] : ['__NONE__']);
    };

    const setAllProducts = (selected: boolean) => {
        onSetReleaseProducts(selected ? [] : ['__NONE__']);
    };

    const setAllCountries = (selected: boolean) => {
        const newCountries = selected ? [] : ['__NONE__'];
        onSetRansomwareFilters({ ...settings.ransomwareFilters, countries: newCountries });
    };

    const setAllGroups = (selected: boolean) => {
        const newGroups = selected ? [] : ['__NONE__'];
        onSetRansomwareFilters({ ...settings.ransomwareFilters, groups: newGroups });
    };

    const isCountrySelected = (c: string) => {
        if (settings.ransomwareFilters.countries.length === 0) return true;
        if (settings.ransomwareFilters.countries.includes('__NONE__')) return false;
        return settings.ransomwareFilters.countries.includes(c);
    };

    const isGroupSelected = (g: string) => {
        if (settings.ransomwareFilters.groups.length === 0) return true;
        if (settings.ransomwareFilters.groups.includes('__NONE__')) return false;
        return settings.ransomwareFilters.groups.includes(g);
    };

    const cardKeys = [
        'metricsRow',
        'cveMetrics',
        'mapWidget',
        'newsCard',
        'ransomwareCard',
        'leaksCard',
        'vulnerabilitiesCard',
        'highVulnerabilitiesCard',   // New
        'mediumVulnerabilitiesCard', // New
        'lowVulnerabilitiesCard',    // New
        'eolCard',
        'releasesCard'
    ] as const;

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full">
                    <Settings className="h-4 w-4" />
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
                <SheetHeader className="px-6 py-4 border-b">
                    <SheetTitle className="flex items-center gap-2">
                        <Settings className="h-5 w-5 text-primary" />
                        {language === 'en' ? 'Dashboard Settings' : 'Ustawienia Dashboardu'}
                    </SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground">
                        {language === 'en'
                            ? 'Resize cards by dragging their edges on the dashboard. Card size determines how many items are shown.'
                            : 'Zmieniaj rozmiar kafelków przeciągając ich krawędzie na dashboardzie. Rozmiar kafelka określa liczbę wyświetlanych elementów.'}
                    </SheetDescription>
                </SheetHeader>

                <Tabs defaultValue="visibility" className="flex-1 flex flex-col overflow-hidden">
                    <TabsList className="w-full justify-start px-4 py-2 bg-muted/50 rounded-none border-b overflow-x-auto">
                        <TabsTrigger value="visibility" className="gap-1.5"><LayoutGrid className="h-4 w-4" /> {language === 'en' ? 'Sections' : 'Sekcje'}</TabsTrigger>
                        <TabsTrigger value="ransomware" className="gap-1.5"><Filter className="h-4 w-4" /> Ransomware</TabsTrigger>
                        <TabsTrigger value="news" className="gap-1.5"><Newspaper className="h-4 w-4" /> News</TabsTrigger>
                        <TabsTrigger value="eol" className="gap-1.5"><CalendarX className="h-4 w-4" /> EOL</TabsTrigger>
                        <TabsTrigger value="releases" className="gap-1.5"><Package className="h-4 w-4" /> Releases</TabsTrigger>
                    </TabsList>

                    {/* Visibility Tab - Card toggles only */}
                    <TabsContent value="visibility" className="flex-1 overflow-hidden m-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-4">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    {language === 'en' ? 'Visible Sections' : 'Widoczne Sekcje'}
                                </h4>
                                {cardKeys.map((key) => (
                                    <div key={key} className="flex items-center justify-between">
                                        <Label htmlFor={key} className="cursor-pointer">
                                            {language === 'en' ? CARD_LABELS[key].en : CARD_LABELS[key].pl}
                                        </Label>
                                        <Switch
                                            id={key}
                                            checked={settings[key] as boolean}
                                            onCheckedChange={() => onToggleCard(key)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    {/* Ransomware Tab */}
                    <TabsContent value="ransomware" className="flex-1 overflow-hidden m-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-6">
                                {/* Countries */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium">{language === 'en' ? 'Countries' : 'Kraje'}</span>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setAllCountries(true)} className="h-7 text-xs">
                                                {language === 'en' ? 'Select All' : 'Zaznacz wszystkie'}
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setAllCountries(false)} className="h-7 text-xs">
                                                {language === 'en' ? 'Deselect All' : 'Odznacz wszystkie'}
                                            </Button>
                                        </div>
                                    </div>
                                    <Input placeholder={language === 'en' ? 'Search countries...' : 'Szukaj krajów...'} value={searchCountry} onChange={e => setSearchCountry(e.target.value)} />
                                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {filteredCountries.slice(0, 50).map(c => (
                                            <div key={c} onClick={() => toggleCountry(c)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isCountrySelected(c) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                                {isCountrySelected(c) && <Check className="h-3 w-3" />}
                                                <span className="truncate">{c}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Groups */}
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-medium">{language === 'en' ? 'Groups' : 'Grupy'}</span>
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setAllGroups(true)} className="h-7 text-xs">
                                                {language === 'en' ? 'Select All' : 'Zaznacz wszystkie'}
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setAllGroups(false)} className="h-7 text-xs">
                                                {language === 'en' ? 'Deselect All' : 'Odznacz wszystkie'}
                                            </Button>
                                        </div>
                                    </div>
                                    <Input placeholder={language === 'en' ? 'Search groups...' : 'Szukaj grup...'} value={searchGroup} onChange={e => setSearchGroup(e.target.value)} />
                                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                        {filteredGroups.slice(0, 50).map(g => (
                                            <div key={g} onClick={() => toggleGroup(g)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isGroupSelected(g) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                                {isGroupSelected(g) && <Check className="h-3 w-3" />}
                                                <span className="truncate">{g}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    {/* News Tab */}
                    <TabsContent value="news" className="flex-1 overflow-hidden m-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-6">
                                <div className="flex gap-2 justify-end mb-2">
                                    <Button size="sm" variant="outline" onClick={() => setAllSources(true)} className="h-7 text-xs">
                                        {language === 'en' ? 'Select All' : 'Zaznacz wszystkie'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setAllSources(false)} className="h-7 text-xs">
                                        {language === 'en' ? 'Deselect All' : 'Odznacz wszystkie'}
                                    </Button>
                                </div>

                                {/* Polish Sources */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-bold flex items-center gap-2">
                                        <ArrowRight className="h-3 w-3" />
                                        {language === 'en' ? 'Polish Sources' : 'Polskie Źródła'}
                                        <Badge variant="secondary" className="ml-auto">{availableSourcesPL.filter(isSourceSelected).length}/{availableSourcesPL.length}</Badge>
                                    </h4>
                                    <div className="grid grid-cols-1 gap-1">
                                        {availableSourcesPL.map(s => (
                                            <div key={s} onClick={() => toggleSource(s)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isSourceSelected(s) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                                <Checkbox checked={isSourceSelected(s)} />
                                                <span>{s}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <Separator className="my-4" />

                                {/* English Sources */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-bold flex items-center gap-2">
                                        <ArrowRight className="h-3 w-3" />
                                        {language === 'en' ? 'English Sources' : 'Angielskie Źródła'}
                                        <Badge variant="secondary" className="ml-auto">{availableSourcesEN.filter(isSourceSelected).length}/{availableSourcesEN.length}</Badge>
                                    </h4>
                                    <div className="grid grid-cols-1 gap-1">
                                        {availableSourcesEN.map(s => (
                                            <div key={s} onClick={() => toggleSource(s)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isSourceSelected(s) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                                <Checkbox checked={isSourceSelected(s)} />
                                                <span>{s}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    {/* Releases Tab */}
                    <TabsContent value="eol" className="flex-1 overflow-hidden m-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-4">
                                <div className="flex gap-2 justify-end">
                                    <Button size="sm" variant="outline" onClick={() => setAllEolProducts(true)} className="h-7 text-xs">
                                        {language === 'en' ? 'Select All' : 'Zaznacz wszystkie'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setAllEolProducts(false)} className="h-7 text-xs">
                                        {language === 'en' ? 'Deselect All' : 'Odznacz wszystkie'}
                                    </Button>
                                </div>
                                <Input placeholder={language === 'en' ? 'Search EOL products...' : 'Szukaj produktów EOL...'} value={searchEolProduct} onChange={e => setSearchEolProduct(e.target.value)} />
                                <div className="grid grid-cols-2 gap-2">
                                    {filteredEolProducts.slice(0, 100).map(p => (
                                        <div key={p} onClick={() => toggleEolProduct(p)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isEolProductSelected(p) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                            {isEolProductSelected(p) && <Check className="h-3 w-3" />}
                                            <span className="truncate">{p}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    {/* Releases Tab */}
                    <TabsContent value="releases" className="flex-1 overflow-hidden m-0">
                        <ScrollArea className="h-full">
                            <div className="p-4 space-y-4">
                                <div className="flex gap-2 justify-end">
                                    <Button size="sm" variant="outline" onClick={() => setAllProducts(true)} className="h-7 text-xs">
                                        {language === 'en' ? 'Select All' : 'Zaznacz wszystkie'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setAllProducts(false)} className="h-7 text-xs">
                                        {language === 'en' ? 'Deselect All' : 'Odznacz wszystkie'}
                                    </Button>
                                </div>
                                <Input placeholder={language === 'en' ? 'Search products...' : 'Szukaj produktów...'} value={searchProduct} onChange={e => setSearchProduct(e.target.value)} />
                                <div className="grid grid-cols-2 gap-2">
                                    {filteredProducts.slice(0, 100).map(p => (
                                        <div key={p} onClick={() => toggleProduct(p)} className={cn("flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm", isProductSelected(p) ? "bg-primary/10 text-primary" : "bg-muted/50")}>
                                            {isProductSelected(p) && <Check className="h-3 w-3" />}
                                            <span className="truncate">{p}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </ScrollArea>
                    </TabsContent>
                </Tabs>

                <SheetFooter className="px-6 py-4 border-t bg-muted/30">
                    <Button variant="outline" onClick={onReset} className="gap-2">
                        <RotateCcw className="h-4 w-4" />
                        {language === 'en' ? 'Reset' : 'Resetuj'}
                    </Button>
                    <SheetClose asChild>
                        <Button className="gap-2">
                            <Check className="h-4 w-4" />
                            {language === 'en' ? 'Done' : 'Gotowe'}
                        </Button>
                    </SheetClose>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
